import { useGetInstitutions } from '@bakerweb/baker-admin';
import { selectedPortfolioAtom } from '@bakerweb/client-state';
import { MHidden, PortfolioActivityDrawer } from '@bakerweb/components';
import { AccountPopover } from '../shared/AccountPopover';
import { ContactPopover } from '../shared/ContactPopover';
import { useCollapseDrawer } from '@bakerweb/theme';
import menu2Fill from '@iconify/icons-eva/menu-2-fill';
import { Icon } from '@iconify/react';
import HistoryIcon from '@mui/icons-material/History';
import { AppBar, Box, CircularProgress, IconButton, Stack, Toolbar, Tooltip, Typography } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { useAtomValue } from 'jotai';
import { useState } from 'react';

const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;

const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 92;

const RootStyle = styled(AppBar)(({ theme }) => ({
  boxShadow: 'none',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
  backgroundColor: alpha(theme.palette.background.default, 0.72),
  [theme.breakpoints.up('lg')]: {
    width: `calc(100% - ${DRAWER_WIDTH + 1}px)`
  }
}));

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: APPBAR_MOBILE,
  [theme.breakpoints.up('lg')]: {
    minHeight: APPBAR_DESKTOP,
    padding: theme.spacing(0, 5)
  }
}));

type DashboardNavbarProps = {
  onOpenSidebar: VoidFunction;
};

export function ClientDashboardNavbar({ onOpenSidebar }: DashboardNavbarProps) {
  const { isCollapse } = useCollapseDrawer();
  const portfolio = useAtomValue(selectedPortfolioAtom);
  const args = portfolio?.institutionId ? [portfolio.institutionId] : null;
  const { institutions, loading } = useGetInstitutions(args);
  const [isPortfolioDrawerOpen, setIsPortfolioDrawerOpen] = useState(false);

  const handleClosePortfolioDrawer = () => {
    setIsPortfolioDrawerOpen(false);
  };

  const getInstitutionInfo = () => {
    const { city, state, institutionName } = institutions[0];
    return `${institutionName} - ${city}, ${state}`;
  };

  return (
    <RootStyle
      sx={{
        ...(isCollapse && {
          width: { lg: `calc(100% - ${COLLAPSE_WIDTH}px)` }
        })
      }}
    >
      <ToolbarStyle>
        <MHidden width="lgUp">
          <IconButton onClick={onOpenSidebar} aria-label="Navigation Button" sx={{ mr: 1, color: 'text.primary' }}>
            <Icon icon={menu2Fill} />
          </IconButton>
        </MHidden>

        <Box sx={{ flexGrow: 1 }}>
          {!loading && institutions.length === 1 ? (
            <Typography variant={'h5'} sx={{ color: 'text.primary' }} noWrap>
              {getInstitutionInfo()}
            </Typography>
          ) : (
            <CircularProgress />
          )}
        </Box>

        <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}>
          <ContactPopover />
          <Tooltip title="Portfolio Activity">
            <IconButton color="primary" onClick={() => setIsPortfolioDrawerOpen(true)}>
              <HistoryIcon fontSize="large" />
            </IconButton>
          </Tooltip>
          <AccountPopover />
        </Stack>
      </ToolbarStyle>
      <PortfolioActivityDrawer open={isPortfolioDrawerOpen} onClose={handleClosePortfolioDrawer} />
    </RootStyle>
  );
}
